import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import CertificationsService from '@/services/CertificationsService'

export const normalizeCertificates = value =>
	(Array.isArray(value) ? value : [])
		.map(item => ({
			certificateId: `${item?.certificateId ?? item?.certificationId ?? item?._id ?? ''}`.trim(),
			certificateName: `${item?.certificateName ?? item?.name ?? item?.nazwa ?? ''}`.trim(),
		}))
		.filter(item => item.certificateId)

export default function useUserCertificationAssignment({
	certificates,
	onCertificatesChange,
	disabled = false,
}) {
	const { t } = useTranslation()
	const [certifications, setCertifications] = useState([])
	const [loading, setLoading] = useState(true)
	const [isAddCertificationModalOpen, setIsAddCertificationModalOpen] = useState(false)
	const [selectedCertification, setSelectedCertification] = useState(null)
	const [searchCertificationTerm, setSearchCertificationTerm] = useState('')

	const assignedCertificates = useMemo(
		() => normalizeCertificates(certificates),
		[certificates]
	)

	const assignedCertificateIds = useMemo(
		() => assignedCertificates.map(item => item.certificateId),
		[assignedCertificates]
	)

	useEffect(() => {
		let isMounted = true

		const loadCertifications = async () => {
			setLoading(true)
			const result = await CertificationsService.getAll({
				limit: 999,
				sortColumn: 'name',
				sortDirection: 'asc',
			})

			if (!isMounted) return

			if (result.error) {
				await Swal.fire(
					t('notifications.errorTitle'),
					result.error || t('trainingTrainers.details.messages.loadFailed'),
					'error'
				)
				setCertifications([])
				setLoading(false)
				return
			}

			setCertifications(Array.isArray(result.data) ? result.data : [])
			setLoading(false)
		}

		loadCertifications()

		return () => {
			isMounted = false
		}
	}, [t])

	const assignedCertifications = useMemo(
		() => assignedCertificates.map(certificate => {
			const fromCatalog = certifications.find(
				item => String(item?._id) === String(certificate.certificateId)
			)

			return {
				_id: certificate.certificateId,
				name: certificate.certificateName || fromCatalog?.name || '—',
				description: fromCatalog?.description || '',
			}
		}),
		[assignedCertificates, certifications]
	)

	const availableCertifications = useMemo(
		() => certifications.filter(certification => !assignedCertificateIds.includes(certification._id)),
		[assignedCertificateIds, certifications]
	)

	const filteredAvailableCertifications = useMemo(() => {
		const searchQuery = `${searchCertificationTerm || ''}`.trim().toLowerCase()
		if (!searchQuery) return availableCertifications

		return availableCertifications.filter(certification => {
			const haystack = [
				certification?.name,
				certification?.description,
			]
				.filter(Boolean)
				.join(' ')
				.toLowerCase()

			return haystack.includes(searchQuery)
		})
	}, [availableCertifications, searchCertificationTerm])

	const handleOpenAddCertificationModal = useCallback(async () => {
		if (disabled || loading) return

		if (!availableCertifications.length) {
			await Swal.fire(
				t('notifications.errorTitle'),
				t('trainingTrainers.details.messages.noAvailableCertifications'),
				'error'
			)
			return
		}

		setSelectedCertification(null)
		setSearchCertificationTerm('')
		setIsAddCertificationModalOpen(true)
	}, [availableCertifications.length, disabled, loading, t])

	const handleCloseAddCertificationModal = useCallback(() => {
		if (disabled) return
		setSelectedCertification(null)
		setSearchCertificationTerm('')
		setIsAddCertificationModalOpen(false)
	}, [disabled])

	const handleAddCertification = useCallback(() => {
		if (!selectedCertification || disabled || !onCertificatesChange) return

		onCertificatesChange([
			...assignedCertificates,
			{
				certificateId: selectedCertification._id,
				certificateName: selectedCertification.name || '',
			},
		])
		setSelectedCertification(null)
		setSearchCertificationTerm('')
		setIsAddCertificationModalOpen(false)
	}, [assignedCertificates, disabled, onCertificatesChange, selectedCertification])

	const handleRemoveCertification = useCallback(async certification => {
		if (disabled || !onCertificatesChange) return

		const result = await Swal.fire({
			title: t('trainingTrainers.details.deleteCertification.confirmTitle'),
			text: t('trainingTrainers.details.deleteCertification.confirmText', {
				name: certification?.name || '—',
			}),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: t('trainingTrainers.details.deleteCertification.confirmButton'),
			cancelButtonText: t('notifications.cancelButton'),
			confirmButtonColor: '#dc2626',
		})

		if (!result.isConfirmed) return

		onCertificatesChange(
			assignedCertificates.filter(item => String(item.certificateId) !== String(certification?._id))
		)
	}, [assignedCertificates, disabled, onCertificatesChange, t])

	return {
		assignedCertifications,
		availableCertifications,
		filteredAvailableCertifications,
		handleAddCertification,
		handleCloseAddCertificationModal,
		handleOpenAddCertificationModal,
		handleRemoveCertification,
		isAddCertificationModalOpen,
		loading,
		searchCertificationTerm,
		selectedCertification,
		setSearchCertificationTerm,
		setSelectedCertification,
	}
}
